import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";

interface MeetingTimePickerProps {
  interval?: number; // in minutes (default: 30)
  onChange?: (from: string, to: string) => void;
}

const MeetingTimePicker: React.FC<MeetingTimePickerProps> = ({
  interval = 30,
  onChange,
}) => {
  const [fromTime, setFromTime] = useState<string>("");
  const [toTime, setToTime] = useState<string>("");

  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  // refs for dropdown lists
  const fromListRef = useRef<HTMLUListElement>(null);
  const toListRef = useRef<HTMLUListElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setFromOpen(false);
        setToOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Generate time slots
  const generateTimeSlots = (step: number) => {
    const slots: string[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += step) {
        const hh = h.toString().padStart(2, "0");
        const mm = m.toString().padStart(2, "0");
        slots.push(`${hh}:${mm}`);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots(interval);

  // Format 24h -> 12h
  const formatTo12Hour = (time: string) => {
    const [hStr, mStr] = time.split(":");
    let h = parseInt(hStr, 10);
    const suffix = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return `${h.toString().padStart(2, "0")}:${mStr} ${suffix}`;
  };

  // Handle start time selection
  const handleFromChange = (value: string) => {
    setFromTime(value);
    setFromOpen(false);

    const index = timeSlots.indexOf(value);
    let nextTime = "";

    if (index !== -1 && index < timeSlots.length - 1) {
      nextTime = timeSlots[index + 1];
    } else {
      nextTime = "00:00";
    }

    setToTime(nextTime);
    onChange?.(value, nextTime);
  };

  // Handle end time selection
  const handleToChange = (value: string) => {
    setToTime(value);
    setToOpen(false);
    onChange?.(fromTime, value);
  };

  // Pick nearest slot >= current time
  const getNearestSlot = () => {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    const nearestMinutes = Math.ceil(minutes / interval) * interval;
    const hh = Math.floor(nearestMinutes / 60).toString().padStart(2, "0");
    const mm = (nearestMinutes % 60).toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };

  // Auto set initial values
  useEffect(() => {
    const nearest = getNearestSlot();
    handleFromChange(nearest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto scroll when dropdown opens
  useEffect(() => {
    if (fromOpen && fromListRef.current) {
      const el = fromListRef.current.querySelector<HTMLLIElement>(
        `li[data-value="${fromTime}"]`
      );
      if (el) el.scrollIntoView({ block: "start" });
    }
  }, [fromOpen, fromTime]);

  useEffect(() => {
    if (toOpen && toListRef.current) {
      const el = toListRef.current.querySelector<HTMLLIElement>(
        `li[data-value="${toTime}"]`
      );
      if (el) el.scrollIntoView({ block: "start" });
    }
  }, [toOpen, toTime]);

  return (
    <div ref={wrapperRef} className="d-flex flex-column gap-2">
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {/* From Time */}
        <div style={{ position: "relative", flex: 1 }}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setFromOpen((prev) => !prev)}
            className={styles.dropdown_field}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setFromOpen((prev) => !prev);
              }
            }}
          >
            {fromTime ? formatTo12Hour(fromTime) : "Start Time"}
          </div>
          {fromOpen && (
            <ul className={styles.dropdown_list} ref={fromListRef}>
              {timeSlots.map((t) => (
                <li
                  key={t}
                  data-value={t} // marks the value (next available slots)
                  onClick={() => handleFromChange(t)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    background: t === fromTime ? "#f1f3f5" : "transparent",
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleFromChange(t);
                    }
                  }}
                >
                  {formatTo12Hour(t)}
                </li>
              ))}
            </ul>
          )}
        </div>

        <span style={{ fontSize: "13px", color: "#888" }}>to</span>

        {/* To Time */}
        <div style={{ position: "relative", flex: 1 }}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => fromTime && setToOpen((prev) => !prev)}
            aria-disabled={!fromTime}
            className={`${styles.dropdown_toggle} ${!fromTime ? "disabled" : ""}`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fromTime && setToOpen((prev) => !prev)
              }
            }}
          >
            {toTime ? formatTo12Hour(toTime) : "End Time"}
          </div>
          {toOpen && (
            <ul className={styles.dropdown_list} ref={toListRef}>
              {timeSlots
                .filter((t) => t > fromTime)
                .map((t) => (
                  <li
                    role="button"
                    tabIndex={0}
                    key={t}
                    data-value={t} // marks the value (next available slots from the start time)
                    onClick={() => handleToChange(t)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleToChange(t);
                      }
                    }}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "14px",
                      background: t === toTime ? "#f1f3f5" : "transparent",
                    }}
                  >
                    {formatTo12Hour(t)}
                  </li>
                ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingTimePicker;
